import { gateway, webLib } from '@pok/weblib';
import { Alchemy, Contract, Network } from 'alchemy-sdk';
import axios from 'axios';
import certificatesAbi from './assets/abi/certificates.json';
import proofsAbi from './assets/abi/proofs.json';
import { base64toBlob } from './utils/conversion.js';
import { getValidatedCertificateAccessToken, saveValidatedCertificateAccessToken } from './utils/sessionStorage.js';
import {
    createValidatedCertificateAccessToken,
    getValidatedCertificate,
    getValidatedCertificateAsset,
} from '@pok/image-store-sdk';
import { applyGateway, getPageById } from '@pok/pages-sdk';
import { getOrganization } from '@pok/rbac-sdk';
import { getPublicLearningRouteCertificates } from '@pok/learning-routes-sdk';
import axiosRetry from 'axios-retry';
import { forgotCertificatePassword, getPublicCertificate } from '@pok/minter-sdk';
import { Buffer } from 'buffer';
import { anonymous } from '@pok/model';
import { certsIndexPendingApprovalCertificates, minterPendingApprovalCertificates } from '@pok/certs-index-sdk';

const config = {
    certificateContractAddress: import.meta.env.VITE_CERTIFICATES_CONTRACT,
    proofsContractAddress: import.meta.env.VITE_PROOFS_CONTRACT,
    alchemy: {
        apiKey: import.meta.env.VITE_ALCHEMY_KEY,
        network: Network[import.meta.env.VITE_ALCHEMY_NETWORK],
    },
};

export const alchemy = new Alchemy({
    apiKey: config.alchemy.apiKey,
    network: config.alchemy.network,
});

const chainTools = alchemy.config.getProvider().then((provider) =>
    provider.getNetwork().then((network) => {
        const proofs = new Contract(config.proofsContractAddress, proofsAbi, provider);
        return {
            proofs: proofs,
            certificates: new Contract(config.certificateContractAddress, certificatesAbi, provider),
            thirdParties: webLib.thirdParties({
                chainId: network.chainId,
                certificatesContractAddress: config.certificateContractAddress,
            }),
            proofsLib: webLib.proofs(proofs, import.meta.env.VITE_PROOFS_API),
        };
    }),
);

export const polygonWalletLink = (chainId, wallet) =>
    chainId === 80002 ? `https://amoy.polygonscan.com/address/${wallet}` : `https://polygonscan.com/address/${wallet}`;

export const links = async () => chainTools.then((c) => c.thirdParties);

export const proofs = async () => chainTools.then((c) => c.proofsLib);

export const validateIdentification = async (certificate, identification) =>
    certificate.tokenId !== undefined
        ? validateWeb3Identification(certificate, identification)
        : validatePokIdentification(certificate, identification);

export const validateWeb3Identification = async (certificate, identification) => {
    return chainTools
        .then((c) => c.certificates)
        .then((certificates) => certificates.verifyIdentification(certificate.tokenId, identification));
};

export const validatePokIdentification = async (certificate, identification) =>
    axios
        .get(`${import.meta.env.VITE_MINTER_API}/certificate/${certificate.id}/validate`, {
            params: {
                identification: identification,
            },
        })
        .then(() => true)
        .catch((e) => (e.response.status === 400 ? false : Promise.reject(e)));

export const certificateExists = async (id) => {
    const client = axios.create({ baseURL: `${import.meta.env.VITE_MINTER_API}/` });

    axiosRetry(client, {
        retries: 25,
        retryCondition: () => true,
        retryDelay: () => 9000,
    });

    return client.get(`certificate/${id}`);
};

export const certificateDetails = async (id) =>
    getPublicCertificate(import.meta.env.VITE_MINTER_API, id).then((cert) => ({
        id: cert.id,
        free: cert.free,
        tokenId: cert.token,
        image: gatewayUrl(cert.imageCid),
        title: cert.title,
        date: cert.issueDate,
        url: cert.url,
        institution: cert.institution,
        snapshot: cert.templateCid,
        minter: cert.minter,
        emissionType: cert.emissionType,
        customPage: cert.customPage,
        learningRoutes: cert.learningRoutes,
        templateType: cert.templateType,
        has2FValidation: cert.has2FValidation,
        currentState: cert.currentState,
        replacedById: cert.replacedById,
        assets: cert.assets,
    }));

export const findPage = (minter, id) =>
    id
        ? getPageById(import.meta.env.VITE_PAGES_API, { id: id }).then((p) => parsePage(minter, p))
        : Promise.resolve(emptyPage);

const parsePage = (wallet, page) => {
    return {
        header: {
            logo: applyGatewayIfApply(wallet, page.definition.header?.logo, page.assetsGatewayUrl),
            color: page.definition.header?.color ?? '#5124A6',
        },
        style: {
            color: page.definition.style?.color ?? '#5124A6',
        },
        banner: {
            image: applyGatewayIfApply(wallet, page.definition.banner?.image, page.assetsGatewayUrl),
            background: page.definition.banner?.background,
            cover: page.definition.banner?.cover,
            alignment: page.definition.banner?.alignment,
        },
        widgets: page.definition.widgets?.map((x) => ({
            ...x,
            payload: {
                ...x.payload,
                image: { url: applyGatewayIfApply(wallet, x.payload.image?.id, page.assetsGatewayUrl) },
            },
        })),
    };
};

export const emptyPage = { banner: {}, header: {}, style: { color: '#5124A6' }, widgets: [] };

const applyGatewayIfApply = (wallet, id, gateway) => {
    if (id) return applyGateway(wallet, id, gateway);
    return undefined;
};

export const templateFromIpfs = async (cid) => axios.get(gatewayUrl(cid)).then((response) => response.data);

export const gatewayUrl = (ipfs) => gateway(ipfs, import.meta.env.VITE_IPFS_GATEWAY);

export const getLinkedinCredential = async (authorizationCode) => {
    const url = `${import.meta.env.VITE_SOCIAL_MEDIA_BRIDGE_API}/credential?authorizationCode=${authorizationCode}`;
    return axios
        .get(url)
        .then((response) => response.data)
        .catch((e) => {
            if (e.response.status === 401) {
                return Promise.reject(new AuthorizationCodeExpired(authorizationCode));
            } else {
                throw e;
            }
        });
};

export const createLinkedinShare = async (accessToken, certificate, title, commentary, postVisibility) => {
    return axios
        .post(`${import.meta.env.VITE_SOCIAL_MEDIA_BRIDGE_API}/certificate/${certificate.id}`, {
            linkedinAccessToken: accessToken,
            title: title,
            commentary: commentary,
            certificateUrl: certificate.url,
            visibility: postVisibility,
        })
        .then((response) => response.data);
};

export const linkedinOrganizationId = async (certificate) => {
    return Promise.resolve(getValidatedCertificateAccessToken(certificate.id)).then((accessToken) =>
        axios
            .post(`${import.meta.env.VITE_SOCIAL_MEDIA_BRIDGE_API}/linkedinOrganizationId`, {
                accessToken,
            })
            .then((response) => response.data.id)
            .catch(() => ''),
    );
};

export const createShare = async (accessToken, certificate, locale) => {
    return axios
        .post(`${import.meta.env.VITE_SOCIAL_MEDIA_BRIDGE_API}/credential/${certificate.id}/share`, {
            locale,
            accessToken,
        })
        .then((response) => response.data);
};

export const getShareOGUrl = async (certificate, locale) =>
    Promise.resolve(getValidatedCertificateAccessToken(certificate.id)).then((accessToken) =>
        createShare(accessToken, certificate, locale).then((res) => res.shareUrl),
    );

export class AuthorizationCodeExpired extends Error {
    constructor(authorizationCode) {
        super(`${authorizationCode} has expired`);
    }
}

export const findPendingCertificates = async (id, token) => {
    if (import.meta.env.VITE_CERTS_INDEX_ENABLED === 'true')
        return certsIndexPendingApprovalCertificates(import.meta.env.VITE_CERTS_INDEX_API, id, token);
    else return minterPendingApprovalCertificates(import.meta.env.VITE_MINTER_API, id, token);
};

export const scheduleCertificate = async (id, tokenId, token) =>
    axios
        .post(`${import.meta.env.VITE_MINTER_API}/schedule/mint/${id}`, {
            token: `${tokenId}:${token}`,
        })
        .catch((e) => (e.response.status === 409 ? Promise.resolve() : Promise.reject(e)));

export const validateAndLoadFromImageStore = async (certificateId, encryptionKey) => {
    return createValidatedCertificateAccessToken(import.meta.env.VITE_IMAGE_STORE_API, encryptionKey, certificateId)
        .then((authResponse) => authResponse.accessToken)
        .then((accessToken) => {
            saveValidatedCertificateAccessToken(certificateId, accessToken);
            return getValidatedCertificate(import.meta.env.VITE_IMAGE_STORE_API, certificateId, accessToken);
        })
        .then((base64image) => {
            return base64toBlob(base64image).then((blob) => URL.createObjectURL(blob));
        });
};

export const loadFromImageStore = async (certificateId, accessToken) => {
    return Promise.resolve(accessToken)
        .then((accessToken) =>
            getValidatedCertificate(import.meta.env.VITE_IMAGE_STORE_API, certificateId, accessToken),
        )
        .then((base64image) => {
            return base64toBlob(base64image).then((blob) => URL.createObjectURL(blob));
        })
        .catch(() => Promise.resolve());
};

export const loadOrganization = async (organizationId) => {
    return getOrganization(import.meta.env.VITE_RBAC_API, organizationId).catch(() => Promise.resolve());
};

export const loadLearningRouteCertificates = async (certificate, learningRoute) => {
    return getPublicLearningRouteCertificates(import.meta.env.VITE_LEARNING_ROUTES_API, {
        certificateId: certificate,
        learningRouteId: learningRoute,
    });
};

export const requestOTCtoChangeCertificate = async (certificateId, email) => {
    return axios
        .post(`${import.meta.env.VITE_CHANGE_REQUESTS_API}/otc`, {
            email: email,
            certificateId: certificateId,
        })
        .then((response) => response.data);
};

export const validateOTCtoChangeCertificate = async (certificateId, otc) => {
    return axios
        .post(`${import.meta.env.VITE_CHANGE_REQUESTS_API}/draft`, {
            otc: otc,
            certificateId: certificateId,
        })
        .then((response) => response.data);
};

export const sendChangeRequest = async (certificateId, token, data) => {
    return axios
        .post(`${import.meta.env.VITE_CHANGE_REQUESTS_API}/commit`, {
            certificateId: certificateId,
            changeRequestId: token,
            firstName: data.first_name,
            lastName: data.last_name,
        })
        .then((response) => response.data);
};

export const forgotPassword = async (certificateId, email) => {
    return forgotCertificatePassword(import.meta.env.VITE_MINTER_API, certificateId, { email });
};

export const getDecryptedAsset = async (certificateId, assetId, token) => {
    return getValidatedCertificateAsset(
        import.meta.env.VITE_IMAGE_STORE_API,
        {
            certificateId: certificateId,
            assetId: assetId,
        },
        anonymous(token),
    ).then((response) => Buffer.from(response.data, 'binary'));
};
